/**
 * Internal Demo Host API Contract (plan 2026-09-13-0029, U1)
 *
 * The backend surface behind `demo.schooljourney.com`. Only the demo host
 * (`apps/demo`, server side) calls these routes; the browser never does.
 *
 * TWO GATES, AND WHICH ROUTE NEEDS WHICH
 *   - Host key: every route requires `x-api-key: DEMO_HOST_API_KEY` through
 *     `createApiKeyMiddleware('DEMO_HOST_API_KEY')`. `INTERNAL_API_KEY` does NOT
 *     open these routes (KTD1).
 *   - Grant: every route except `exchange` and `links/redeem` also requires a
 *     backend-signed grant in the {@link DEMO_GRANT_HEADER} header (KTD5). The
 *     host holds no Clerk secret and cannot mint one; it only relays the grant
 *     the backend gave it, wrapped in its own cookie. A presenter grant is
 *     re-checked against Clerk's `isSuperAdmin` on every call.
 *
 *   | Route                                     | Key | Grant             |
 *   |-------------------------------------------|-----|-------------------|
 *   | POST /internal/demo/exchange              | yes | —                 |
 *   | GET  /internal/demo/personas              | yes | presenter or link |
 *   | POST /internal/demo/mint                  | yes | presenter or link |
 *   | POST /internal/demo/mint/ack              | yes | presenter or link |
 *   | POST /internal/demo/links                 | yes | presenter         |
 *   | GET  /internal/demo/links                 | yes | presenter         |
 *   | POST /internal/demo/links/:linkId/revoke  | yes | presenter         |
 *   | POST /internal/demo/links/redeem          | yes | —                 |
 *   | POST /internal/demo/app-preview           | yes | presenter or link |
 *   | GET  /internal/demo/app-preview/:id       | yes | presenter or link |
 *   | POST /internal/demo/app-preview/:id/end   | yes | presenter or link |
 *
 * WHY A HEADER AND NOT THE BODY. `GET /personas` and `GET /links` carry a grant
 * and have no body — `fetch` refuses a body on a GET — so one transport for
 * every route keeps `require-grant.ts` reading one place.
 *
 * REFUSALS. Every refusal these routes answer carries the standard backend
 * error envelope `{ error: { type, message, details? } }` with `type` one of
 * {@link DEMO_REFUSAL_CODES}, un-prefixed, at the status
 * {@link DEMO_REFUSAL_HTTP_STATUS} names. The host maps on `type`, never on the
 * status or the message. Messages name variables and codes, never values
 * (R23): no token, grant, ticket or email appears in one.
 *
 * Ids on the wire are public ids: `DemoLink.publicId`, `DemoMint.publicId` and
 * `UsersOnSchools.publicId` (a persona). Timestamps are ISO-8601 strings.
 */

import { z } from 'zod'
import { DemoClerkSessionIdSchema, DemoHandoffNonceSchema, DemoHandoffSchoolSchema } from '../demo/handoff'

// ============================================================================
// Caps, defaults and fixed values
// ============================================================================

/** Successful mints per presenter per rolling hour (R12a). */
export const DEMO_PRESENTER_MINTS_PER_HOUR = 30
/** Successful mints per link per rolling hour (R12a). */
export const DEMO_LINK_MINTS_PER_HOUR = 10
/** Successful mints across every actor per rolling hour (R12a). */
export const DEMO_FLEET_MINTS_PER_HOUR = 200
/** Refusal rows per actor per rolling hour; past it a refusal writes no row and emits no event (R12). */
export const DEMO_REFUSALS_PER_HOUR = 60
/** A link's lifetime mint allowance when the creator does not choose one (R13). */
export const DEMO_LINK_DEFAULT_MAX_MINTS = 50
/** Upper bound on a chosen lifetime allowance — a sanity bound, not a product number. */
export const DEMO_LINK_MAX_MINTS_CEILING = 500
/** A link's lifetime in days when the creator does not choose one (R13). */
export const DEMO_LINK_DEFAULT_TTL_DAYS = 7
/** The longest a link may live, in days (R13). */
export const DEMO_LINK_MAX_TTL_DAYS = 30
/** Active (unrevoked, unexpired) links one presenter may hold at once (R13). */
export const DEMO_ACTIVE_LINKS_PER_PRESENTER = 10
/** Longest link label, in characters. */
export const DEMO_LINK_LABEL_MAX_LENGTH = 120
/** Most personas one link may carry. */
export const DEMO_LINK_MAX_PERSONAS = 20
/** Clerk sign-in ticket lifetime (R11). Fixed after D9 answers Q1; the fallback is 300. */
export const DEMO_TICKET_TTL_SECONDS = 120
/** How long after the ticket's TTL an ack is still accepted (KTD16). */
export const DEMO_ACK_GRACE_SECONDS = 60
/** Hard maximum age of a demo persona session; the sweep revokes anything older (R12b). */
export const DEMO_SESSION_MAX_AGE_HOURS = 12
/** Presenter grant (and presenter cookie) lifetime (R6). */
export const DEMO_PRESENTER_GRANT_TTL_HOURS = 8
/** Ceiling on a link grant's lifetime; the grant also never outlives its link (R14). */
export const DEMO_LINK_GRANT_MAX_TTL_HOURS = 24

/** The only origin allowed to mint; anything else asserted in `hostOrigin` trips the R22 tripwire. */
export const DEMO_HOST_PRODUCTION_ORIGIN = 'https://demo.schooljourney.com' as const

/** Request header carrying the backend-signed grant on every grant-gated route. */
export const DEMO_GRANT_HEADER = 'x-demo-grant' as const

/** Prefix of every prospect link token: `sjdemo_<linkId>_<secret>` (KTD10). */
export const DEMO_LINK_TOKEN_PREFIX = 'sjdemo_' as const
/** Random bytes in a link token's secret half, before base64url encoding (43 characters). */
export const DEMO_LINK_TOKEN_SECRET_BYTES = 32

// ============================================================================
// Shared vocabulary
// ============================================================================

/** Who is asking: a signed-in super-admin, or the holder of a prospect link. */
export const DemoActorKindSchema = z.enum(['presenter', 'link'])
export type DemoActorKind = z.infer<typeof DemoActorKindSchema>

/**
 * `DemoMint.outcome`. One value per ledger row, written before the response
 * returns. There is no outcome for a refusal past the budget — that writes no
 * row — nor for a grant or presenter refusal, which is answered before the
 * mint use-case runs.
 */
export const DemoMintOutcomeSchema = z.enum([
  'minted',
  'refused_cap',
  'refused_link',
  'refused_target',
  'refused_upstream'
])
export type DemoMintOutcome = z.infer<typeof DemoMintOutcomeSchema>

/**
 * Every refusal code the `/internal/demo/*` routes answer with, grouped by
 * where it arises. The host passes these through to its own clients unchanged
 * (see {@link DemoHostErrorCodeSchema}).
 */
export const DEMO_REFUSAL_CODES = [
  // Request shape — any route.
  'INVALID_REQUEST',
  // Grant gate — any grant-gated route, before the use-case runs; no ledger row.
  'GRANT_INVALID', // missing, forged, malformed or expired grant
  'GRANT_KIND_FORBIDDEN', // a link grant on a presenter-only route (R17)
  'PRESENTER_NOT_SUPER_ADMIN', // exchange, or the per-call presenter re-check (R8)
  // Exchange.
  'CLERK_TOKEN_INVALID', // wrong issuer, algorithm or azp, or expired
  // Mint — each writes a ledger row (see DEMO_MINT_REFUSAL_OUTCOME) except REFUSAL_BUDGET.
  'CAP_REACHED', // presenter, link hourly, link lifetime or fleet cap; `details.cap` says which
  'REFUSAL_BUDGET', // past DEMO_REFUSALS_PER_HOUR for this actor: no row, no event
  'TARGET_REFUSED', // not an eligible demo persona (R10, R18); also refuses link creation
  'UPSTREAM', // Clerk refused or failed to mint the ticket
  // Self-mint (presenter only): the presenter holds no active school membership; no row.
  'PRESENTER_NO_MEMBERSHIP',
  'LINK_REVOKED',
  'LINK_EXPIRED',
  'PERSONA_NOT_ALLOWED', // a link mint for a persona outside the link's set
  // Link redeem — an unknown id and a wrong secret answer identically (KTD10).
  'LINK_NOT_FOUND',
  // Ack.
  'MINT_NOT_FOUND', // no such row for THIS grant's actor
  'ACK_WINDOW_CLOSED', // later than createdAt + DEMO_TICKET_TTL_SECONDS + DEMO_ACK_GRACE_SECONDS
  'ACK_ALREADY_RECORDED', // the session id is write-once
  'SESSION_MISMATCH', // the session is not the target's, or predates the mint
  // Link create.
  'ACTIVE_LINK_LIMIT', // the creator already holds DEMO_ACTIVE_LINKS_PER_PRESENTER active links
  'TEACHER_OPT_IN_REQUIRED', // a teacher persona without `teacherOptIn: true`
  // App preview (`demo/app-preview.ts`).
  'NO_SCHOOL_APP', // the chosen school app (or the demo school's own) is not in the registry or not buildable
  'PREVIEW_NOT_FOUND', // no such preview, or one another actor started
  'PREVIEWS_OFF', // the backend has no mobile lab configured
  'LAB_UNAVAILABLE' // the lab could not be reached or refused the preview
] as const
export const DemoRefusalCodeSchema = z.enum(DEMO_REFUSAL_CODES)
export type DemoRefusalCode = z.infer<typeof DemoRefusalCodeSchema>

/** The HTTP status each refusal is answered with. */
export const DEMO_REFUSAL_HTTP_STATUS: Readonly<Record<DemoRefusalCode, number>> = {
  INVALID_REQUEST: 400,
  GRANT_INVALID: 401,
  GRANT_KIND_FORBIDDEN: 403,
  PRESENTER_NOT_SUPER_ADMIN: 403,
  CLERK_TOKEN_INVALID: 401,
  CAP_REACHED: 429,
  REFUSAL_BUDGET: 429,
  TARGET_REFUSED: 403,
  UPSTREAM: 502,
  PRESENTER_NO_MEMBERSHIP: 403,
  LINK_REVOKED: 410,
  LINK_EXPIRED: 410,
  PERSONA_NOT_ALLOWED: 403,
  LINK_NOT_FOUND: 404,
  MINT_NOT_FOUND: 404,
  ACK_WINDOW_CLOSED: 409,
  ACK_ALREADY_RECORDED: 409,
  SESSION_MISMATCH: 409,
  ACTIVE_LINK_LIMIT: 409,
  TEACHER_OPT_IN_REQUIRED: 400,
  NO_SCHOOL_APP: 409,
  PREVIEW_NOT_FOUND: 404,
  PREVIEWS_OFF: 503,
  LAB_UNAVAILABLE: 502
}

/** The refusal codes a mint records in the ledger, and the outcome each one writes. */
export const DemoLedgerRefusalCodeSchema = z.enum([
  'CAP_REACHED',
  'TARGET_REFUSED',
  'UPSTREAM',
  'LINK_REVOKED',
  'LINK_EXPIRED',
  'PERSONA_NOT_ALLOWED'
])
export type DemoLedgerRefusalCode = z.infer<typeof DemoLedgerRefusalCodeSchema>

/** `DemoMint.outcome` for each ledger refusal; `DemoMint.refusalCode` stores the code itself. */
export const DEMO_MINT_REFUSAL_OUTCOME: Readonly<Record<DemoLedgerRefusalCode, Exclude<DemoMintOutcome, 'minted'>>> = {
  CAP_REACHED: 'refused_cap',
  TARGET_REFUSED: 'refused_target',
  UPSTREAM: 'refused_upstream',
  LINK_REVOKED: 'refused_link',
  LINK_EXPIRED: 'refused_link',
  PERSONA_NOT_ALLOWED: 'refused_link'
}

/** Which cap a `CAP_REACHED` hit — carried in `details.cap` and in the refusal log line. */
export const DemoMintCapSchema = z.enum(['presenter_hourly', 'link_hourly', 'link_lifetime', 'fleet_hourly'])
export type DemoMintCap = z.infer<typeof DemoMintCapSchema>

/** The backend error envelope as these routes fill it. */
export const DemoRefusalResponseSchema = z.object({
  error: z.object({
    type: DemoRefusalCodeSchema,
    message: z.string(),
    details: z.unknown().optional()
  })
})
export type DemoRefusalResponse = z.infer<typeof DemoRefusalResponseSchema>

/**
 * Codes the demo host's own `/api/*` routes answer its browser with: every
 * backend refusal passed through unchanged, plus the refusals only the host can
 * make. A backend 5xx or an unparseable backend answer surfaces as `UPSTREAM`.
 *
 * - `UNAUTHENTICATED` — no host cookie, or one that fails verification.
 * - `ORIGIN_REJECTED` — the CSRF check failed (KTD6).
 * - `HOST_NOT_PRODUCTION` — the host refused to call the backend at all:
 *   not the production deployment on the production host (R22).
 * - `LINK_INVALID` — the landing page's neutral answer for a link that is
 *   unknown, expired or revoked; the prospect is never told which (U9).
 */
export const DemoHostErrorCodeSchema = z.enum([
  ...DEMO_REFUSAL_CODES,
  'UNAUTHENTICATED',
  'ORIGIN_REJECTED',
  'HOST_NOT_PRODUCTION',
  'LINK_INVALID'
])
export type DemoHostErrorCode = z.infer<typeof DemoHostErrorCodeSchema>

/** Error body of the demo host's own `/api/*` routes. */
export const DemoHostErrorResponseSchema = z.object({
  error: z.object({
    code: DemoHostErrorCodeSchema,
    message: z.string()
  })
})
export type DemoHostErrorResponse = z.infer<typeof DemoHostErrorResponseSchema>

// ============================================================================
// Grants (KTD5)
// ============================================================================

/**
 * A grant on the wire: a compact JWS, HS256-signed by the backend with
 * `DEMO_GRANT_SECRET`. Opaque to the host, which stores it inside its own cookie
 * and relays it in {@link DEMO_GRANT_HEADER}; only the backend decodes it.
 */
export const DemoGrantSchema = z.string().min(1).max(4096)
export type DemoGrant = z.infer<typeof DemoGrantSchema>

/** `exp` / `iat` in the JWT convention: whole seconds since the epoch. */
const EpochSecondsSchema = z.number().int().positive()

/**
 * Presenter grant payload. `sid` is the Clerk session the exchange verified —
 * one grant per `sid` — and the id the host's client signs out after the
 * exchange (KTD3). Lives {@link DEMO_PRESENTER_GRANT_TTL_HOURS}.
 */
export const DemoPresenterGrantPayloadSchema = z.object({
  kind: z.literal('presenter'),
  clerkUserId: z.string().startsWith('user_'),
  email: z.string().email(),
  sid: DemoClerkSessionIdSchema,
  iat: EpochSecondsSchema,
  exp: EpochSecondsSchema
})
export type DemoPresenterGrantPayload = z.infer<typeof DemoPresenterGrantPayloadSchema>

/**
 * Link grant payload. `exp` = min(link expiry, now + {@link DEMO_LINK_GRANT_MAX_TTL_HOURS});
 * every link-grant call re-reads the link row, so revocation needs no grant
 * tracking.
 */
export const DemoLinkGrantPayloadSchema = z.object({
  kind: z.literal('link'),
  linkId: z.string().min(1).describe('DemoLink public id'),
  iat: EpochSecondsSchema,
  exp: EpochSecondsSchema
})
export type DemoLinkGrantPayload = z.infer<typeof DemoLinkGrantPayloadSchema>

export const DemoGrantPayloadSchema = z.discriminatedUnion('kind', [
  DemoPresenterGrantPayloadSchema,
  DemoLinkGrantPayloadSchema
])
export type DemoGrantPayload = z.infer<typeof DemoGrantPayloadSchema>

// ============================================================================
// Exchange — POST /internal/demo/exchange (host key only)
// ============================================================================

/**
 * The host forwards the Clerk session token its client read with `getToken()`
 * (never from a cookie). The backend verifies it — issuer pinned to the
 * production instance, RS256, `azp` = {@link DEMO_HOST_PRODUCTION_ORIGIN},
 * unexpired — and requires the top-level `isSuperAdmin` claim (R5).
 */
export const DemoExchangeRequestSchema = z.object({
  clerkToken: z.string().min(1).max(8192)
})
export type DemoExchangeRequest = z.infer<typeof DemoExchangeRequestSchema>

/**
 * `sessionId` is the Clerk session the client must now sign out by id, and only
 * after this 200 (KTD3). `expiresAt` is the grant's expiry, which the host
 * copies onto its presenter cookie.
 */
export const DemoExchangeResponseSchema = z.object({
  grant: DemoGrantSchema,
  kind: z.literal('presenter'),
  sessionId: DemoClerkSessionIdSchema,
  expiresAt: z.string().describe('Grant expiry, ISO-8601'),
  presenter: z.object({
    clerkUserId: z.string(),
    email: z.string()
  })
})
export type DemoExchangeResponse = z.infer<typeof DemoExchangeResponseSchema>

// ============================================================================
// Personas — GET /internal/demo/personas (presenter or link grant)
// ============================================================================

/** Active roles a persona can hold at the demo school. `faculty` is the teacher. */
export const DemoPersonaRoleSchema = z.enum(['student', 'parent', 'faculty'])
export type DemoPersonaRole = z.infer<typeof DemoPersonaRoleSchema>

/** Display label for each role, so the presenter page and the link form agree. */
export const DEMO_PERSONA_ROLE_LABELS: Readonly<Record<DemoPersonaRole, string>> = {
  student: 'Student',
  parent: 'Parent',
  faculty: 'Teacher'
}

/**
 * One persona a presenter (or a link holder) may sign in as. Shaped like the
 * dev-login picker's user item, with typed roles. Only members who pass the
 * KTD9 predicate appear: active, at the `platform = 'demo'` school, not
 * `isAdmin`, not super-admin, no membership anywhere else (R18).
 */
export const DemoPersonaSchema = z.object({
  id: z.string().describe('UsersOnSchools public id — what mint and link creation carry'),
  firstName: z.string(),
  lastName: z.string(),
  preferredFirstName: z.string(),
  photoUrlThumbnail: z.string(),
  roles: z.array(DemoPersonaRoleSchema)
})
export type DemoPersona = z.infer<typeof DemoPersonaSchema>

/** A teacher persona needs an explicit opt-in to be attached to a link (R13). */
export function isDemoTeacherPersona(persona: Pick<DemoPersona, 'roles'>): boolean {
  return persona.roles.includes('faculty')
}

/**
 * With a presenter grant: every eligible persona. With a link grant: only the
 * link's set, re-read from the row on every call (R15), and `link` carries the
 * expiry the link-mode banner shows; it is `null` for a presenter.
 */
export const DemoPersonasResponseSchema = z.object({
  school: DemoHandoffSchoolSchema,
  data: z.array(DemoPersonaSchema),
  link: z
    .object({
      expiresAt: z.string().describe('Link expiry, ISO-8601')
    })
    .nullable()
})
export type DemoPersonasResponse = z.infer<typeof DemoPersonasResponseSchema>

// ============================================================================
// Mint — POST /internal/demo/mint (presenter or link grant)
// ============================================================================

/**
 * `hostOrigin` is the origin the host believes it is serving; anything but
 * {@link DEMO_HOST_PRODUCTION_ORIGIN} still mints but logs the
 * `[DEMO_MINT] origin-mismatch` tripwire (R22). `nonce` is echoed back so the
 * host can hand the frame a ticket message without holding state between the
 * two calls.
 */
export const DemoMintRequestSchema = z.object({
  personaId: z.string().min(1).max(191).describe('UsersOnSchools public id of the persona'),
  nonce: DemoHandoffNonceSchema,
  hostOrigin: z.string().url()
})
export type DemoMintRequest = z.infer<typeof DemoMintRequestSchema>

/**
 * `ticket`, `nonce` and `school` are exactly what the host posts to the frame
 * as the KTD7 `ticket` message. `mintId` comes back on the ack. The host sends
 * this body to its browser with `Cache-Control: private, no-store`.
 */
export const DemoMintResponseSchema = z.object({
  mintId: z.string().describe('DemoMint public id'),
  ticket: z.string().min(1),
  nonce: DemoHandoffNonceSchema,
  school: DemoHandoffSchoolSchema,
  expiresAt: z.string().describe('Ticket expiry, ISO-8601')
})
export type DemoMintResponse = z.infer<typeof DemoMintResponseSchema>

// ============================================================================
// Self-mint — POST /internal/demo/self-mint (presenter grant only; SCH-1805)
// ============================================================================

/**
 * The presenter signs the frame in as THEMSELVES: a sign-in ticket for the
 * grant's own Clerk user, behind "Sign in as me" on the presenter page `/` (the
 * app inside the isolated frame then offers its own super-admin Impersonate
 * screen). No persona id: the target is the grant. The answer is a
 * {@link DemoMintResponse}, and the ack path is the same as a persona mint's.
 */
export const DemoSelfMintRequestSchema = z.object({
  nonce: DemoHandoffNonceSchema,
  hostOrigin: z.string().url()
})
export type DemoSelfMintRequest = z.infer<typeof DemoSelfMintRequestSchema>

// ============================================================================
// Ack — POST /internal/demo/mint/ack (presenter or link grant)
// ============================================================================

/**
 * The frame's `signed-in` report, relayed. Accepted once, only for a row the
 * grant's own actor minted, within the ticket TTL plus
 * {@link DEMO_ACK_GRACE_SECONDS}, and only when Clerk says the session belongs
 * to the target and was created after the mint (KTD16).
 */
export const DemoMintAckRequestSchema = z.object({
  mintId: z.string().min(1).max(191),
  sessionId: DemoClerkSessionIdSchema
})
export type DemoMintAckRequest = z.infer<typeof DemoMintAckRequestSchema>

export const DemoMintAckResponseSchema = z.object({
  mintId: z.string(),
  sessionId: DemoClerkSessionIdSchema,
  sessionAckedAt: z.string().describe('ISO-8601')
})
export type DemoMintAckResponse = z.infer<typeof DemoMintAckResponseSchema>

// ============================================================================
// Links (presenter grant) and redeem (host key only)
// ============================================================================

/**
 * A link's state, derived — nothing stores it. Precedence when several apply:
 * revoked, then expired, then exhausted.
 */
export const DemoLinkStatusSchema = z.enum(['active', 'exhausted', 'expired', 'revoked'])
export type DemoLinkStatus = z.infer<typeof DemoLinkStatusSchema>

/** Derive {@link DemoLinkStatus} from a link row's fields. */
export function deriveDemoLinkStatus(
  link: { revokedAt: Date | string | null; expiresAt: Date | string; mintCount: number; maxMints: number },
  now: Date = new Date()
): DemoLinkStatus {
  if (link.revokedAt !== null) return 'revoked'
  if (new Date(link.expiresAt).getTime() <= now.getTime()) return 'expired'
  if (link.mintCount >= link.maxMints) return 'exhausted'
  return 'active'
}

/** One link as the `/links` panel shows it. Never carries the token or its hash. */
export const DemoLinkSummarySchema = z.object({
  id: z.string().describe('DemoLink public id'),
  label: z.string(),
  personaSchoolUserIds: z.array(z.string()),
  status: DemoLinkStatusSchema,
  createdByClerkUserId: z.string(),
  createdByEmail: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  maxMints: z.number().int(),
  mintCount: z.number().int(),
  openCount: z.number().int(),
  lastUsedAt: z.string().nullable(),
  createdAt: z.string()
})
export type DemoLinkSummary = z.infer<typeof DemoLinkSummarySchema>

/**
 * POST /internal/demo/links. Every persona must pass the KTD9 predicate
 * (`TARGET_REFUSED` otherwise) and a teacher needs `teacherOptIn`
 * (`TEACHER_OPT_IN_REQUIRED`). Defaults fill `ttlDays` and `maxMints`.
 */
export const DemoLinkCreateRequestSchema = z.object({
  label: z.string().trim().min(1).max(DEMO_LINK_LABEL_MAX_LENGTH),
  personaSchoolUserIds: z
    .array(z.string().min(1).max(191))
    .min(1, 'A link needs at least one persona.')
    .max(DEMO_LINK_MAX_PERSONAS)
    .refine(ids => new Set(ids).size === ids.length, { message: 'Persona ids must be unique.' }),
  ttlDays: z.number().int().min(1).max(DEMO_LINK_MAX_TTL_DAYS).default(DEMO_LINK_DEFAULT_TTL_DAYS),
  maxMints: z.number().int().min(1).max(DEMO_LINK_MAX_MINTS_CEILING).default(DEMO_LINK_DEFAULT_MAX_MINTS),
  teacherOptIn: z.boolean().default(false)
})
export type DemoLinkCreateRequest = z.infer<typeof DemoLinkCreateRequestSchema>
/** The body a caller sends, before defaults: `ttlDays`, `maxMints` and `teacherOptIn` optional. */
export type DemoLinkCreateRequestInput = z.input<typeof DemoLinkCreateRequestSchema>

/**
 * `token` is the plaintext `sjdemo_<linkId>_<secret>`, returned by this call
 * and never again (R13); only its secret's sha256 is stored. The host builds
 * the prospect URL `${DEMO_HOST_ORIGIN}/go#${token}` itself.
 */
export const DemoLinkCreateResponseSchema = z.object({
  link: DemoLinkSummarySchema,
  token: z.string()
})
export type DemoLinkCreateResponse = z.infer<typeof DemoLinkCreateResponseSchema>

/**
 * GET /internal/demo/links — the demo school's links, newest first.
 * `activeForPresenter` counts the calling presenter's active links against
 * `activeLimit` ({@link DEMO_ACTIVE_LINKS_PER_PRESENTER}).
 */
export const DemoLinkListResponseSchema = z.object({
  data: z.array(DemoLinkSummarySchema),
  activeForPresenter: z.number().int().nonnegative(),
  activeLimit: z.number().int().positive()
})
export type DemoLinkListResponse = z.infer<typeof DemoLinkListResponseSchema>

/**
 * POST /internal/demo/links/:linkId/revoke — no body. Idempotent: revoking a
 * revoked link answers 200 with the row unchanged. `sessionsRevoked` counts the
 * Clerk sessions this call ended (KTD16); `null` when that revocation could not
 * run (the link is revoked regardless, and the hourly sweep retries).
 */
export const DemoLinkRevokeResponseSchema = z.object({
  link: DemoLinkSummarySchema,
  sessionsRevoked: z.number().int().nonnegative().nullable()
})
export type DemoLinkRevokeResponse = z.infer<typeof DemoLinkRevokeResponseSchema>

/**
 * The shape of a prospect token: `sjdemo_`, the link's public id (a cuid:
 * lowercase letters and digits), `_`, then the base64url secret. The secret may
 * itself contain `_`, so the id is everything up to the FIRST `_` after the
 * prefix. Shape only — whether the token is real is the backend's constant-time
 * compare.
 */
export const DemoLinkTokenSchema = z
  .string()
  .max(512)
  .regex(/^sjdemo_[a-z0-9]+_[A-Za-z0-9_-]{43,}$/, 'Not a demo link token')

/** Split a well-formed token into its link id and secret, or `null`. */
export function parseDemoLinkToken(token: string): { linkId: string; secret: string } | null {
  if (!DemoLinkTokenSchema.safeParse(token).success) return null
  const rest = token.slice(DEMO_LINK_TOKEN_PREFIX.length)
  const separator = rest.indexOf('_')
  return { linkId: rest.slice(0, separator), secret: rest.slice(separator + 1) }
}

/**
 * POST /internal/demo/links/redeem (host key only — the prospect has no grant
 * yet). Unknown id and wrong secret both answer `LINK_NOT_FOUND`; a revoked or
 * expired link answers `LINK_REVOKED` / `LINK_EXPIRED` only once the secret has
 * matched. Increments `openCount` and `lastUsedAt`.
 */
export const DemoLinkRedeemRequestSchema = z.object({
  token: DemoLinkTokenSchema
})
export type DemoLinkRedeemRequest = z.infer<typeof DemoLinkRedeemRequestSchema>

/**
 * `expiresAt` is the grant's expiry (the host's link cookie copies it);
 * `linkExpiresAt` is the link's own, for the banner.
 */
export const DemoLinkRedeemResponseSchema = z.object({
  grant: DemoGrantSchema,
  kind: z.literal('link'),
  linkId: z.string(),
  expiresAt: z.string().describe('Grant expiry, ISO-8601'),
  linkExpiresAt: z.string().describe('Link expiry, ISO-8601')
})
export type DemoLinkRedeemResponse = z.infer<typeof DemoLinkRedeemResponseSchema>
